"use client";

import Image from "next/image";
import Link from "next/link";

import { Instagram, LinkedIn } from "@/components/icons";
import { RichTextField } from "@/components/typography/RichTextField";
import { ExpandableText } from "@/components/typography/ExpandableText";
import { cn } from "@/lib/utils";

type ExpertCardProps = {
  name: string;
  title?: string;
  text?: string;
  image?: { filename: string; alt?: string };
  href?: string;
  instagramUrl?: string;
  linkedinUrl?: string;
  className?: string;
  onPreviewClick?: () => void;
};

const ExpertCard = ({
  name,
  title,
  text,
  image,
  href,
  instagramUrl,
  linkedinUrl,
  className,
  onPreviewClick,
}: ExpertCardProps) => {
  const hasLinks = !onPreviewClick && (instagramUrl || linkedinUrl);

  const cardClasses = cn(
    "bg-sand-10 rounded-3xl p-3 flex flex-col gap-6 h-full",
    (href || onPreviewClick) && "cursor-pointer",
    className,
  );

  return (
    <div
      className={cardClasses}
      onClick={onPreviewClick || (href ? () => window.open(href, "_blank", "noopener,noreferrer") : undefined)}
    >
      {image?.filename && (
        <div className="relative w-full h-[155px] rounded-3xl overflow-hidden">
          <Image
            src={image.filename}
            alt=""
            fill
            sizes="280px"
            className="object-cover blur-xl scale-110"
            aria-hidden="true"
          />
          <Image
            src={image.filename}
            alt={image.alt || name}
            fill
            sizes="280px"
            className="object-contain"
          />
        </div>
      )}

      <div className="flex flex-col items-center text-center text-black">
        <h3 className="text-headline-l">{name}</h3>
        {title && <p className="text-copy">{title}</p>}
      </div>

      {text && (
        <ExpandableText lines={4} className="text-center">
          <RichTextField value={text} className="text-copy text-black text-center" />
        </ExpandableText>
      )}

      {hasLinks && (
        <div className="flex flex-col gap-2 items-center mt-auto">
          <span className="text-copy-sm text-gray-60">Kontakt</span>
          <div className="flex gap-2">
            {instagramUrl && (
              <Link
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <Instagram className="size-10 text-black" />
              </Link>
            )}
            {linkedinUrl && (
              <Link
                href={linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <LinkedIn className="size-10 text-black" />
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export { ExpertCard };
